import type { RepairConfig, RepairKey, RepairReportEntry } from '../lib/types';

interface RepairBarProps {
  repairs: RepairConfig;
  report: RepairReportEntry[];
  busy: boolean;
  onToggle: (key: RepairKey) => void;
  onApplyAll: () => void;
  onReset: () => void;
}

export function RepairBar({ repairs, report, busy, onToggle, onApplyAll, onReset }: RepairBarProps) {
  const changed = (key: RepairKey) => report.find((entry) => entry.key === key)?.changed ?? 0;

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 shadow-panel sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">1-click deterministic repairs</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Rule-based and idempotent — re-running on repaired data changes nothing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onApplyAll}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:border-slate-500 hover:text-white disabled:opacity-40"
          >
            Apply all
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onReset}
            className="rounded-lg border border-slate-800 px-3 py-1.5 text-xs text-slate-500 transition hover:border-slate-600 hover:text-slate-300 disabled:opacity-40"
          >
            Reset
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {report.map((entry) => {
          const active = repairs[entry.key];
          const n = changed(entry.key);
          return (
            <button
              key={entry.key}
              type="button"
              disabled={busy}
              onClick={() => onToggle(entry.key)}
              title={entry.label}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ring-1 transition disabled:opacity-40 ${
                active
                  ? 'bg-amber-500/15 text-amber-200 ring-amber-500/40'
                  : 'bg-slate-800/60 text-slate-400 ring-slate-700 hover:text-slate-200'
              }`}
            >
              {active ? '✓ ' : ''}{entry.label}
              {n > 0 && <span className="ml-1.5 opacity-70">({n})</span>}
            </button>
          );
        })}
      </div>
    </section>
  );
}
